import {
  Inject,
  inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { RESPONSE } from '../../express.tokens';
import { Response } from 'express';
import { Router } from '@angular/router';
import { isPlatformServer } from '@angular/common';

type AppRequestOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  transferCache?: boolean | { includeHeaders?: string[] };
  responseType?: 'json' | 'text';
};

@Injectable({
  providedIn: 'root',
})
export class AppService {
  platformId = inject(PLATFORM_ID);
  router = inject(Router);

  private headerMenuOpen = new BehaviorSubject<boolean>(false);
  currentheaderMenuOpen = this.headerMenuOpen.asObservable();

  lastStatusCode = signal<number>(200);

  getUrl = '';
  constructor(
    private httpClient: HttpClient,
    @Optional() @Inject(RESPONSE) response: any,
    @Optional() @Inject('ORIGIN') private origin: string,
  ) {}

  public isServer(): boolean {
    return isPlatformServer(this.platformId);
  }

  get(
    url: string,
    options?: { transferCache?: boolean | { includeHeaders?: string[] } },
  ): Promise<any> {
    const getUrl = environment.apiDomain + url;
    const isServer = this.isServer();
    const requestStart =
      isServer && typeof performance !== 'undefined' ? performance.now() : 0;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    };

    if (isServer) {
      if (this.origin) {
        headers['origin'] = this.origin;
      }
      process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0';
    }

    return this.httpClient
      .get<any>(getUrl, {
        headers,
        observe: 'response',
        transferCache: options?.transferCache,
      })
      .toPromise()
      .then((response: any) => {
        // if (isServer) {
        //   console.log(
        //     `[SSR timing] api get ${url} ${Math.round(performance.now() - requestStart)}ms status=${response.status}`,
        //   );
        // }
        this.lastStatusCode.set(response.status);
        return this.extractData(response.body);
      })
      .catch((error: any) => {
        // if (isServer) {
        //   console.error(
        //     `[SSR timing] api get failed ${url} ${Math.round(performance.now() - requestStart)}ms status=${error?.status ?? 'unknown'}`,
        //   );
        // }
        if (error.status) {
          this.lastStatusCode.set(error.status);
        }
        return this.handleError(error);
      });
  }

  request<T>(
    method: 'DELETE' | 'GET' | 'POST' | 'PUT',
    url: string,
    options?: AppRequestOptions,
  ): Promise<T> {
    return this.requestResponse<T>(method, url, options).then(
      (response) => response.body as T,
    );
  }

  requestResponse<T>(
    method: 'DELETE' | 'GET' | 'POST' | 'PUT',
    url: string,
    options?: AppRequestOptions,
  ): Promise<HttpResponse<T>> {
    const isServer = this.isServer();

    if (isServer) {
      process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0';
    }

    const headers = this.createRequestHeaders(options?.headers);

    return firstValueFrom(
      this.httpClient.request<T>(method, this.buildApiUrl(url), {
        body: options?.body,
        headers,
        observe: 'response',
        responseType: options?.responseType ?? 'json',
        transferCache: options?.transferCache,
      } as any) as Observable<HttpResponse<T>>,
    )
      .then((response) => {
        this.lastStatusCode.set(response.status);
        return response;
      })
      .catch((error: any) => {
        if (error.status) {
          this.lastStatusCode.set(error.status);
        }

        return Promise.reject(error);
      });
  }

  postData(url: string, body: string): Promise<any> {
    if (this.isServer()) {
      process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0';
    }

    const postUrl = environment.apiDomain + url;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    };

    if (this.isServer()) {
      if (this.origin) {
        headers['origin'] = this.origin;
      }
    }

    return this.httpClient
      .post<any>(postUrl, body, { headers })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  private buildApiUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    return environment.apiDomain + url.replace(/^\/+/, '');
  }

  private createRequestHeaders(
    headers?: Record<string, string>,
  ): Record<string, string> {
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
      ...headers,
    };

    if (this.isServer() && this.origin && !requestHeaders['origin']) {
      requestHeaders['origin'] = this.origin;
    }

    return requestHeaders;
  }
  private extractData(res: Response) {
    const body = JSON.stringify(res);
    return body || {};
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  toggleMenuOpen(status: boolean) {
    this.headerMenuOpen.next(status);
  }
}
